import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import { computeKpis } from "@/utils/kpi";
import { formatCOP, formatNumber } from "@/utils/format";

const FinancialHealth = () => {
  const { data } = useApp();
  const kpi = computeKpis(data, data.period);
  const [adSpend, setAdSpend] = useState(250000);

  const stalled = useMemo(() => {
    const now = Date.now();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return data.products
      .map((p) => {
        const item = data.inventory.find((i) => i.productId === p.id);
        const units = item
          ? item.stockCasa +
            item.stockPuntoClear +
            Object.values(item.stockByAdvisor).reduce((s, n) => s + n, 0)
          : 0;
        const lastSale = data.sales
          .filter((s) => s.productId === p.id)
          .sort((a, b) => +new Date(b.date) - +new Date(a.date))[0];
        const days = lastSale
          ? Math.floor((now - +new Date(lastSale.date)) / (24 * 60 * 60 * 1000))
          : 999;
        return { product: p, units, days };
      })
      .filter((x) => x.units > 0 && (x.days > 30 || x.days === 999))
      .sort((a, b) => b.days - a.days)
      .slice(0, 4);
  }, [data.products, data.inventory, data.sales]);

  const roi = adSpend > 0 ? ((kpi.grossProfit - adSpend) / adSpend) * 100 : 0;
  const roiPositive = roi >= 0;

  return (
    <div className="space-y-4">
      <div className="glass-panel rounded-lg p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
            <i className="ri-heart-pulse-line" />
          </span>
          <h3 className="editable-title text-base font-bold">Salud Financiera</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg bg-background-200/40 border border-background-300/40 p-3">
            <p className="text-[11px] font-semibold text-foreground-500 flex items-center gap-1.5">
              <i className="ri-refresh-line text-accent-300" /> Rotación de inventario
            </p>
            {stalled.length === 0 ? (
              <p className="text-xs text-foreground-600 mt-2">
                Todo el stock tiene rotación saludable. Sin alertas por ahora.
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {stalled.map((x) => (
                  <li key={x.product.id} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-foreground-800 truncate">{x.product.name}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary-500/25 text-primary-100 whitespace-nowrap">
                      {x.days === 999 ? "Sin ventas" : `${x.days} días`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-foreground-500 mt-2">
              Alerta: stock estancado con más de 30 días sin movimiento.
            </p>
          </div>

          <div className="rounded-lg bg-background-200/40 border border-background-300/40 p-3">
            <p className="text-[11px] font-semibold text-foreground-500 flex items-center gap-1.5">
              <i className="ri-advertisement-line text-accent-300" /> ROI de publicidad
            </p>
            <label className="block text-[10px] text-foreground-500 mt-2 mb-1">
              Inversión publicitaria del período (COP)
            </label>
            <input
              type="number"
              value={adSpend}
              onChange={(e) => setAdSpend(Number(e.target.value) || 0)}
              className="w-full bg-background-100/70 border border-background-300/50 rounded-md px-3 py-2 text-sm text-foreground-950 outline-none focus:border-accent-500/50"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-foreground-600">ROI estimado</span>
              <span
                className={`text-lg font-heading font-extrabold ${
                  roiPositive ? "text-accent-300" : "text-primary-200"
                }`}
              >
                {roiPositive ? "+" : ""}
                {formatNumber(Math.round(roi))}%
              </span>
            </div>
            <p className="text-[10px] text-foreground-500 mt-1">
              Compara utilidad bruta ({formatCOP(kpi.grossProfit)}) frente a la inversión en pauta.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialHealth;